// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, IconName} from '@wandb/weave/components/Icon';
import React from 'react';
import {components, FormatOptionLabelMeta, MenuProps} from 'react-select';

import {maybePluralize} from '../../../../util/uihelpers';
import * as S from './SelectArtifactCollection.styles';

export function getValueForNewType(artifactType: string) {
  return `New ${artifactType}`;
}

type CollectionType = 'portfolio' | 'sequence';

type GroupedArtifactCollectionOption = {
  label: string;
  options: ArtifactCollectionOption[];
};

type SelectArtifactCollectionProps = {
  options: GroupedArtifactCollectionOption[];
  defaultCollectionValue: string;
  disabled?: boolean;
  onSelectArtifactCollection: (
    artifactSequence: ArtifactCollectionOption
  ) => void;
  includeCreateCollection?: boolean;
  onCreateClick?: () => void;
  artifactType?: string;
  setFirstOptionAsDefault?: boolean;
  loading?: boolean;
};

export type ArtifactCollectionOption = {
  value: string;
  name: string;
  artifactType?: string;
  collectionType?: CollectionType;
  description?: string;
  numVersions?: number;
};

const OptionLabel = (
  props: ArtifactCollectionOption,
  context: FormatOptionLabelMeta<ArtifactCollectionOption>
) => {
  const {name, artifactType, collectionType, numVersions} = props;
  const selected = context.selectValue[0];
  const isSelected = selected === props;
  const isNewCollectionOption = name.includes('Register new');
  if (isSelected) {
    return (
      <S.SelectOptionLabel>
        <S.SelectOptionLabelText>{name}</S.SelectOptionLabelText>
      </S.SelectOptionLabel>
    );
  }
  let iconName: IconName = 'versions-layers';
  if (collectionType === 'portfolio' && artifactType === 'model') {
    iconName = 'model';
  } else if (isNewCollectionOption) {
    iconName = 'add-new';
  }

  return (
    <S.SelectOption>
      <S.SelectOptionLabelIcon name={iconName} />
      <S.SelectOptionLabel>
        <S.SelectOptionLabelText>{name}</S.SelectOptionLabelText>
        {!isNewCollectionOption && numVersions !== undefined && (
          <S.SelectOptionSubLabelText>
            {maybePluralize(numVersions, 'version', 's')}
          </S.SelectOptionSubLabelText>
        )}
      </S.SelectOptionLabel>
    </S.SelectOption>
  );
};

export const SelectArtifactCollection = ({
  options,
  defaultCollectionValue,
  disabled,
  onSelectArtifactCollection,
  includeCreateCollection,
  onCreateClick,
  artifactType,
  setFirstOptionAsDefault,
  loading,
}: SelectArtifactCollectionProps) => {
  const onChange = (option: any) => {
    if (option?.value === getValueForNewType(artifactType ?? '')) {
      onSelectArtifactCollection({
        value: '',
        name: '',
      });
      onCreateClick?.();
    } else if (option != null) {
      onSelectArtifactCollection(option);
    }
  };

  let defaultOption;
  for (const group of options) {
    if (defaultOption) {
      break;
    }

    for (const option of group.options) {
      if (option.value === defaultCollectionValue) {
        defaultOption = option;
        break;
      }
    }
  }

  if (setFirstOptionAsDefault && !defaultOption && options?.length > 0) {
    defaultOption = options[0].options?.[0];
  }
  const newCollectionOption = {
    value: getValueForNewType(artifactType ?? ''),
    name: `Create new '${artifactType}' collection`,
  };

  // To save state for the selected option
  if (defaultCollectionValue === getValueForNewType(artifactType ?? '')) {
    defaultOption = newCollectionOption;
  }

  const customSelectComponents = {
    Menu: (
      props: MenuProps<
        ArtifactCollectionOption,
        false,
        GroupedArtifactCollectionOption
      >
    ) => {
      return (
        <components.Menu {...props}>
          {props.children}
          <S.OptionCreateNewArtifactOuter>
            <S.OptionCreateNewArtifact
              isSelected={
                defaultCollectionValue ===
                getValueForNewType(artifactType ?? '')
              }>
              <S.SelectOption
                data-test="select-artifact-collection-create-new"
                onClick={e => {
                  props.selectProps.onChange(newCollectionOption, {
                    action: 'select-option',
                    option: newCollectionOption,
                  });
                }}>
                <Icon name="add-new" />
                <S.SelectOptionLabelText>
                  {newCollectionOption.name}
                </S.SelectOptionLabelText>
              </S.SelectOption>
            </S.OptionCreateNewArtifact>
          </S.OptionCreateNewArtifactOuter>
        </components.Menu>
      );
    },
  };
  return (
    <Select<ArtifactCollectionOption, false, GroupedArtifactCollectionOption>
      options={options}
      defaultValue={defaultOption}
      formatOptionLabel={OptionLabel}
      getOptionValue={(option: ArtifactCollectionOption) => option.name}
      placeholder="Select collection for artifact linking"
      onChange={onChange}
      groupDivider={includeCreateCollection}
      isDisabled={disabled || loading}
      components={includeCreateCollection ? customSelectComponents : undefined}
    />
  );
};
